import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuid } from 'uuid';
import { useWebSocketAssinatura } from '../hooks/useWebSocketAssinatura';
import {
  creditCardSchema,
  CreditCardSchema,
} from '../components/HirePlanForm/data/schema GDF/creditCardSchema';
import {
  debitSchema,
  DebitSchema,
} from '../components/HirePlanForm/data/schema GDF/debitSchema';
import {
  billetSchema,
  BilletSchema,
} from '../components/HirePlanForm/data/schema GDF/billetSchema';
import {
  consignadoSchema,
  ConsignadoSchema,
} from '../components/HirePlanForm/data/schema GDF/consignadoSchema';
import { useHirePlanFormStepStore } from '../stores/useHirePlanFormStepStore';
import {
  usePaymentStore,
  useValorTitularStore,
} from '../stores/usePaymentStore';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { useError } from '../stores/useError';
import { useVidaCountStore } from '../stores/useQuantidadeVidas';
import { useResponseStore } from '../stores/useResponseStore';
import { usePaPStore } from '../stores/PaPStore';
import { showImportantAlert } from '../utils/Alert';
import { useLinkStore } from '../stores/useLinkStore';
import { useCepStore } from '../stores/useCepStore';

type PaymentFormSchema = CreditCardSchema &
  DebitSchema &
  BilletSchema &
  ConsignadoSchema;

interface HirePlanFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  goToNextForm: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
  navigateToProposta: () => void;
}

const HirePlanFormContext = createContext({} as HirePlanFormContextProps);

interface HirePlanFormProviderProps {
  children: ReactNode;
}

export function HirePlanFormProvider({
  children,
}: HirePlanFormProviderProps): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const goToNextStep = useHirePlanFormStepStore(state => state.goToNextStep);
  const paymentId = usePaymentStore(state => state.id);
  const navigate = useNavigate();
  const storedResponse = useResponseStore(x => x.response);
  const vidas = useVidaCountStore(x => x.count);
  const valorTitular = useValorTitularStore(x => x.valor);
  const cepVia = useCepStore(x => x.cepVia);

  const setLoading = useLoadingStore(state => state.setLoading);
  const setError = useError(state => state.setError);
  const setTitle = useError(state => state.setErrorTitle);
  const setText = useError(state => state.setErrorText);
  const setResponse = useResponseStore(x => x.setResponse);
  const PaP = usePaPStore(x => x.PAP);
  const setIframe = useLinkStore(state => state.setLinkData);
  const gdfIds = ['993', '998', '999', '1000'];
  const TjdftIds = ['1044', '1045', '1046'];
  const Dnit = ['1035', '1036', '1037'];
  const PrefPontGross = ['1054'];
  const { aguardarAssinatura } = useWebSocketAssinatura();

  const isGdf = gdfIds.includes(
    String(initialPlanData?.produtoComercial?.id_prodcomerc),
  );
  const isTjdft = TjdftIds.includes(
    String(initialPlanData?.produtoComercial?.id_prodcomerc),
  );
  const isDnit = Dnit.includes(
    String(initialPlanData?.produtoComercial?.id_prodcomerc),
  );
  const isPrefPontGross = PrefPontGross.includes(
    String(initialPlanData?.produtoComercial?.id_prodcomerc),
  );
  interface CheckoutData {
    token: string;
    cpf?: string;
    verifica: boolean;
    nomeTitular?: string;
    dataNascimento?: string;
    emailTitular?: string;
    cep?: string;
    idUf?: string;
    endereco?: string;
    numeroCasa?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    celular?: string;
    nomeMae?: string;
    matricula?: string;
    cargo?: string;
    Contratacao?: number;
    orgaoOrigem?: string;
    matriculaOrigem?: string;
    idSexo?: string;
    idEstadoCivil?: string;
    formaPagamento?: object;
    proposta: string;
    cepVia: boolean;
    valor_Mensalidade: number;
    vencimentoBoleto?: string;
    vencimentoDebito?: string;
    vencimentoConsignado?: string;
    qtdVidas: string;
    dependentes?: object;
    dependenteSamePlan: boolean;
    idBanco?: string;
    conta?: string;
    agencia?: string;
    operacao?: string;
    primeiraBoleto?: boolean;
    responsavelFinanceiro: object;
    idOrgaoExpedidor: number;
    idOrgaoExpedidorUf: number;
    idFontePagadora: number;
    orgao: number;
  }

  function getSchemaResolver() {
    switch (paymentId) {
      //Credit Card
      case 1: {
        return creditCardSchema;
      }

      case 2: {
        //Debit
        return debitSchema;
      }

      case 3: {
        //Billet
        return billetSchema;
      }

      case 4: {
        //Consignado
        return consignadoSchema;
      }

      default: {
        return creditCardSchema;
      }
    }
  }

  const calculateDueDate = (): string => {
    const today = new Date();
    today.setDate(1);
    today.setMonth(today.getMonth() + 1);

    return today.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  function getPaymentData(data: PaymentFormSchema, banco?: number) {
    switch (paymentId) {
      case 1: {
        //Credit Card
        return {
          formaPagamento: {
            gpPagto: 1,
            idPagto: 16,
          },
        };
      }
      case 2: {
        //Debit
        const pagamento = initialPlanData.bancos.find(
          x => x.id_banco === Number(banco),
        );
        return {
          formaPagamento: {
            gpPagto: 2,
            idPagto: pagamento?.id_meiopagto_if || 6,
          },
        };
      }
      case 3: {
        //Billet
        return {
          formaPagamento: {
            gpPagto: 3,
            idPagto: 4,
          },
        };
      }
      case 4: {
        //Consignado
        return {
          formaPagamento: {
            gpPagto: 4,
            idPagto: data?.payment?.Type || 3,
          },
        };
      }
      default: {
        //consignado
        return {
          formaPagamento: {
            gpPagto: 4,
            idPagto: data?.payment?.Type || 3,
          },
        };
      }
    }
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormSchema>({
    defaultValues: {
      payment: {
        firstPaymentBillet: false,
      },
    },
    resolver: zodResolver(getSchemaResolver()),
  });

  const goToNextForm: SubmitHandler<PaymentFormSchema> = () => {
    goToNextStep();
  };

  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    setLoading(true);

    const banco = watch('payment.bank');
    const conta = watch('payment.debitAcount');
    const agencia = watch('payment.agency');
    const operacao = watch('payment.operation');
    const primeiraBoleto = watch('payment.firstPaymentBillet');
    const vencimentoBoleto = watch('payment.billetDate');
    const vencimentoDebito = watch('payment.debitDate');
    const VencimentoGdf = calculateDueDate();
    const flowId = uuid();

    const formaPagamento = getPaymentData(data, Number(banco));
    const matricula = isGdf
      ? (data.user?.documents?.matricula?.replace(/[.-]/g, '') || '').padStart(
          8,
          '0',
        )
      : data.user?.documents?.matricula;

    const checkoutData: CheckoutData = {
      token: initialPlanData.token,
      cpf: data.user?.documents?.cpf?.replace(/\D/g, ''),
      nomeTitular: data.user?.fullName,
      dataNascimento: data.user?.birthDate,
      emailTitular: data.user?.email,
      cep: data.user?.address?.cep,
      idUf: data.user?.address?.uf,
      verifica: true,
      endereco: data.user?.address?.street,
      numeroCasa: String(Number(data.user?.address?.number)),
      complemento: data.user?.address?.complement,
      bairro: data.user?.address?.neighborhood,
      cidade: data.user?.address?.city,
      celular: data.user?.phone,
      nomeMae: data.user?.fullMothersName,
      matricula: matricula,
      cargo: data.user.cargo,
      Contratacao: Number(data.user.Contratacao) || 0,
      orgaoOrigem: data.user.lotacao,
      matriculaOrigem: data.user.matriculalotacao,
      idSexo: data.user?.gender,
      idEstadoCivil: data.user?.civilStatus,
      idFontePagadora: Number(data.user?.fontePagamento || 5),
      idOrgaoExpedidor: 63,
      idOrgaoExpedidorUf: 8,
      orgao: parseFloat(data.user?.orgao || '0'),
      cepVia: cepVia,
      formaPagamento: isGdf
        ? {
            gpPagto:
              initialPlanData?.formasPagamento.find(x => x.id_meiopagto_if != 0)
                ?.cd_gmeiopagto || 4,
            idPagto: initialPlanData?.formasPagamento.find(
              x => x.id_meiopagto_if != 0,
            )?.id_meiopagto_if,
          }
        : isTjdft
        ? {
            gpPagto: 4,
            idPagto: 5,
          }
        : isDnit
        ? {
            gpPagto: 4,
            idPagto: 2,
          }
        : isPrefPontGross
        ? {
            gpPagto: 5,
            idPagto: 14,
          }
        : formaPagamento.formaPagamento,
      proposta: String(Math.floor(Math.random() * 1000000000)),
      valor_Mensalidade: parseFloat(valorTitular.replace(',', '.')),
      vencimentoBoleto,
      vencimentoDebito,
      vencimentoConsignado: VencimentoGdf,
      qtdVidas: vidas,
      dependenteSamePlan: false,
      dependentes: data.user?.dependents?.map(dependent => ({
        nome: dependent.name,
        cpf: dependent.cpf?.replace(/\D/g, ''),
        plano: dependent.plano || data.user?.plano,
        valor_plano:
          dependent.plano_vl || parseFloat(valorTitular.replace(',', '.')),
        dataNascimento: dependent.birthDate,
        nomeMae: dependent.mothersName,
        idSexo: dependent.gender,
        idParentesco: dependent.parentage,
        idOrgaoExpedidor: 63,
        idOrgaoExpedidorUf: 8,
      })),
      idBanco: banco,
      conta,
      agencia,
      operacao,
      primeiraBoleto: primeiraBoleto || false,
      responsavelFinanceiro: {
        cpf: data.user?.documents?.cpf?.replace(/\D/g, ''),
        nome: data.user?.fullName,
        dataNascimento: data.user?.birthDate,
        email: data.user?.email,
        cep: data.user?.address?.cep,
        endereco: data.user?.address?.street,
        cidade: data.user?.address?.city,
        numero: String(data.user?.address?.number),
        complemento: data.user?.address?.complement,
        bairro: data.user?.address?.neighborhood,
        idUf: data.user?.address?.uf,
        telefone: data.user?.phone,
      },
    };

    const UrlEnvio = PaP
      ? `${process.env.REACT_APP_OG_API_URL}/ws/solicitacao`
      : process.env.REACT_APP_OG_PAYMENT_INDIVIDUAL;

    const JsonEnvio: any = PaP
      ? { sender: flowId, payment: checkoutData }
      : checkoutData;

    try {
      const formatData = new FormData();
      formatData.append('dados', JSON.stringify(JsonEnvio));
      if (data.user.Contracheque?.file?.[0]) {
        formatData.append('files[comprovante]', data.user.Contracheque.file[0]);
      }
      if (data.user.Identidade?.file?.[0]) {
        formatData.append('files[documento]', data.user.Identidade.file[0]);
      }
      const response = await fetch(UrlEnvio, {
        method: 'POST',
        body: formatData,
      });

      if (response.ok) {
        if (PaP) {
          const responseData = await response.json();
          if (responseData) {
            setIframe(responseData.confirmationLink);
          }
          goToNextStep();
          await aguardarAssinatura({
            flowId,
            onConfirm: async (data: any) => {
              if (data.retorno) {
                if (data.retorno.pagamento) {
                  alert('Solicitação confirmada com sucesso!');
                  setResponse(data.retorno.pagamento);
                  goToNextStep();
                }
              }
            },
          });
        } else {
          const responseData = await response.json();
          setLoading(false);
          setError(false);
          setResponse(responseData.data);
          setIframe(responseData.data.AssinaturaHash?.Iframe);
          if (responseData.data?.AssinaturaHash?.Iframe) {
            await aguardarAssinatura({
              flowId,
              onConfirm: async (data: any) => {
                if (data.retorno) {
                  if (data.retorno.pagamento) {
                    alert('Solicitação confirmada com sucesso!');
                    setResponse(data.retorno.pagamento);
                  }
                }
              },
            });
          }
          if (initialPlanData?.corretora?.id_corretora !== 21903) {
            showImportantAlert();
          }
          goToNextStep();
        }
      } else {
        const errorData = await response.json();
        setTitle(errorData.code || 'Erro');
        setText(errorData.error || errorData.message);
        alert(
          `Erro: ${
            errorData.error || errorData.message || response.statusText
          }`,
        );
        setLoading(false);
        setError(true);
        throw new Error(errorData.error || errorData.message);
      }
    } catch (error: any) {
      setLoading(false);
      setError(true);
      setTitle(error.code || 'Erro');
      setText(error.Error || error.message);
      throw new Error(error.Error || error);
    }
  };
  const navigateToProposta = () => {
    if (storedResponse) {
      navigate('/proposta', { state: storedResponse });
    }
  };

  return (
    <HirePlanFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        goToNextForm,
        errors,
        clearErrors,
        unregister,
        setValue,
        navigateToProposta,
      }}
    >
      {children}
    </HirePlanFormContext.Provider>
  );
}

export function useHirePlanForm(): HirePlanFormContextProps {
  return useContext(HirePlanFormContext);
}
